import { supabase } from '@/integrations/supabase/client';
import type { MarsPriceTier } from '@/lib/mars-prices';

const marsPriceColumnMap: Record<MarsPriceTier, string> = {
  pesin: 'pesin_price',
  '30': 'price_30',
  '60': 'price_60',
  '90': 'price_90',
  '120': 'price_120',
  '150': 'price_150',
  '180': 'price_180',
};

/**
 * Unit cost for "Güncel Mars Fiyatlarına Göre Stok Değeri": latest Mars tier price for SKU,
 * else products.current_cost / initial_cost (same as Reports processProductAnalytics valueMars).
 */
export function getMarsInventoryUnitPrice(
  product: { item_code?: string | null; current_cost?: number | null; initial_cost?: number | null } | null | undefined,
  marsPriceMap: Map<string, number>
): number {
  const lastReceivedCost = product?.current_cost ?? product?.initial_cost ?? 0;
  const sku = product?.item_code;
  const marsPrice = sku ? marsPriceMap.get(sku) : undefined;
  if (marsPrice != null && marsPrice !== undefined && !Number.isNaN(marsPrice)) {
    return marsPrice;
  }
  return lastReceivedCost;
}

/**
 * On-hold line unit price: Mars tier price → products.price → on_holds.hold_price.
 */
export function getOnHoldUnitPrice(
  product: { item_code?: string | null; price?: number | string | null } | null | undefined,
  marsPriceMap: Map<string, number>,
  holdPrice: number | string | null | undefined
): number {
  const sku = product?.item_code;
  const mars = sku ? marsPriceMap.get(sku) : undefined;
  if (mars != null && !Number.isNaN(mars) && mars > 0) {
    return mars;
  }

  const raw = product?.price;
  const productPrice =
    raw === null || raw === undefined ? NaN : typeof raw === 'string' ? parseFloat(raw) : Number(raw);
  if (Number.isFinite(productPrice) && productPrice > 0) {
    return productPrice;
  }

  const h = holdPrice;
  const hold =
    h === null || h === undefined ? NaN : typeof h === 'string' ? parseFloat(h) : Number(h);
  if (Number.isFinite(hold) && hold > 0) {
    return hold;
  }

  return 0;
}

/** Latest Mars price per SKU for the given tier (matches Reports inventory Mars value fetch). */
export async function fetchLatestMarsPriceMapForSkus(
  skus: string[],
  tier: MarsPriceTier
): Promise<Map<string, number>> {
  const marsPriceMap = new Map<string, number>();
  const uniqueSkus = [...new Set(skus.filter(Boolean))];
  const marsPriceColumn = marsPriceColumnMap[tier];

  if (uniqueSkus.length === 0) return marsPriceMap;

  const batchSize = 100;
  for (let i = 0; i < uniqueSkus.length; i += batchSize) {
    const skuBatch = uniqueSkus.slice(i, i + batchSize);

    const { data: marsPricesData, error: marsPricesError } = await supabase
      .from('mars_product_prices')
      .select('sku, pesin_price, price_30, price_60, price_90, price_120, price_150, price_180, price_date')
      .in('sku', skuBatch)
      .order('price_date', { ascending: false });

    if (marsPricesError) {
      console.error('Error fetching Mars prices:', marsPricesError);
      continue;
    }

    if (marsPricesData) {
      const skuLatestPriceMap = new Map<string, { price: string; price_date: string }>();
      marsPricesData.forEach((priceRecord: Record<string, unknown>) => {
        const skuKey = priceRecord.sku as string;
        const existing = skuLatestPriceMap.get(skuKey);
        const recordDate = String(priceRecord.price_date ?? '');
        if (!existing || new Date(recordDate) > new Date(existing.price_date)) {
          const priceVal = priceRecord[marsPriceColumn];
          skuLatestPriceMap.set(skuKey, {
            price: priceVal != null ? String(priceVal) : '',
            price_date: recordDate,
          });
        }
      });

      skuLatestPriceMap.forEach((priceData, sku) => {
        const price = parseFloat(priceData.price);
        if (!Number.isNaN(price) && price > 0) {
          marsPriceMap.set(sku, price);
        }
      });
    }
  }

  return marsPriceMap;
}
